import type { ReactNode } from 'react'

import { GlassCard } from '@/components/ui/GlassCard'
import { Icon } from '@/components/ui/Icon'
import { Pill } from '@/components/ui/Pill'
import { BadgeCheck, Clock, Flag, Sparkles, UserPlus } from '@/lib/icons'
import type { LucideIcon } from '@/lib/icons'

interface StepTileProps {
  n: number
  title: string
  children: string
  /** Optional decorative background layer (image or gradient). */
  backdrop?: ReactNode
}

function StepTile({ n, title, children, backdrop }: StepTileProps) {
  return (
    <GlassCard className="relative flex flex-col gap-2 overflow-hidden p-6 sm:col-span-2 lg:col-span-4">
      {backdrop}
      <span className="bg-surface-container-lowest/70 text-primary border-primary-container/30 font-display relative z-10 flex size-8 items-center justify-center rounded-md border text-base font-bold">
        {n}
      </span>
      <h3 className="font-display text-headline-md text-fg relative z-10">
        {title}
      </h3>
      <p className="font-body text-body-sm text-fg-muted relative z-10">
        {children}
      </p>
    </GlassCard>
  )
}

interface IconTileProps {
  icon: LucideIcon
  title: string
  children: string
  className?: string
}

function IconTile({ icon, title, children, className }: IconTileProps) {
  return (
    <GlassCard className={`flex flex-col gap-2 p-6 ${className ?? ''}`}>
      <Icon icon={icon} className="text-primary size-6" />
      <h3 className="font-display text-headline-md text-fg">{title}</h3>
      <p className="font-body text-body-sm text-fg-muted">{children}</p>
    </GlassCard>
  )
}

export function HowItWorks() {
  return (
    <section aria-labelledby="how-heading" className="space-y-8">
      <div className="mx-auto max-w-2xl space-y-4 text-center md:mx-0 md:text-left">
        <Pill tone="primary">How it works</Pill>
        <h2
          id="how-heading"
          className="text-headline-lg-mobile text-fg md:text-headline-lg"
        >
          One of you saves. The other earns.
        </h2>
        <p className="font-body text-body-md text-fg-muted">
          Every link here has two sides. Whoever doesn&apos;t get the money gets
          the points — so it&apos;s always worth posting, and always worth
          claiming.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12">
        <StepTile
          n={1}
          title="Browse"
          backdrop={
            <>
              <img
                src="/images/bento-network.jpg"
                srcSet="/images/bento-network-sm.jpg 352w, /images/bento-network.jpg 704w"
                sizes="(min-width: 1024px) 33vw, 100vw"
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                width={704}
                height={430}
                className="absolute inset-0 size-full object-cover opacity-25"
              />
              <div
                aria-hidden="true"
                className="from-surface-dim via-surface-dim/70 absolute inset-0 bg-gradient-to-b to-transparent"
              />
            </>
          }
        >
          Search live links by brand or category. You see the brand, the offer,
          and the days left before you sign up for anything.
        </StepTile>

        <StepTile n={2} title="Claim or post">
          Claim a link to use it, or post one of your own. Posting takes a URL
          and about ten seconds.
        </StepTile>

        <StepTile
          n={3}
          title="Earn"
          backdrop={
            <div
              aria-hidden="true"
              className="from-primary-container/15 to-secondary-container/10 absolute inset-0 bg-gradient-to-br"
            />
          }
        >
          Points go to whoever didn&apos;t get the external reward. They stack
          up in your stash as a record of what you&apos;ve put in.
        </StepTile>

        <IconTile
          icon={UserPlus}
          title="Referral links"
          className="lg:col-span-6"
        >
          The brand pays the poster. So the claimer gets the points.
        </IconTile>
        <IconTile
          icon={Sparkles}
          title="Discount links"
          className="lg:col-span-6"
        >
          The claimer saves money. So the poster gets the points.
        </IconTile>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <IconTile icon={BadgeCheck} title="Screened before they go live">
          Every URL is checked, and anything suspicious is held back for review.
        </IconTile>
        <IconTile icon={Clock} title="Fresh when you need it">
          Every link carries the poster&apos;s real expiry date and shows the
          days remaining. Expired ones leave the feed automatically.
        </IconTile>
        <IconTile icon={Flag} title="Policed by the people using them">
          Anything broken or dodgy gets reported and pulled.
        </IconTile>
      </div>
    </section>
  )
}
